// src/components/EtkinlikFormu.js

import React, { useState, useEffect } from 'react';
import { 
    Calendar, MapPin, Link as LinkIcon, Users, 
    Ticket, ShieldCheck, Globe, Camera, X, Check,
    Clock, AlertCircle, Sparkles
} from 'lucide-react';
import { callApi } from '../api';
import ImageUploader from './ImageUploader';
import AutoTextarea from './AutoTextarea';

const EtkinlikFormu = ({ etkinlikToEdit, kulup, onSave, onCancel }) => {
    const initialState = {
        baslik: '',
        aciklama: '',
        etkinlikTarihi: '',
        yer: '',
        resimURL: '',
        konumLink: '',
        fiyat: '',
        kontenjan: '',
        yedekKontenjan: '10',
        kayitBaslangic: '',
        kayitBitis: '',
        iptalSureLimitiSaat: '24',
        katilimSarti: 'UYE_ZORUNLU',
    };

    const [formData, setFormData] = useState(initialState);
    const [isFree, setIsFree] = useState(true);
    const [showUploader, setShowUploader] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (etkinlikToEdit) {
            const rawDate = etkinlikToEdit.etkinlikTarihi || etkinlikToEdit.tarih;
            let tarihLocal = '';

            if (rawDate) {
                if (rawDate.length === 16 && rawDate.includes('T')) {
                    tarihLocal = rawDate;
                } else {
                    const d = new Date(rawDate);
                    const localIso = new Date(d.getTime() - (d.getTimezoneOffset() * 60000)).toISOString();
                    tarihLocal = localIso.slice(0, 16);
                }
            }

            const formatIsoForInput = (isoStr) => {
                if (!isoStr) return '';
                if (isoStr.length === 16 && isoStr.includes('T')) return isoStr;
                try {
                    const d = new Date(isoStr);
                    const localIso = new Date(d.getTime() - (d.getTimezoneOffset() * 60000)).toISOString();
                    return localIso.slice(0, 16);
                } catch { return ''; }
            };

            const priceVal = etkinlikToEdit.fiyat !== undefined ? etkinlikToEdit.fiyat : '';
            setIsFree(!priceVal || Number(priceVal) === 0);

            setFormData({
                baslik: etkinlikToEdit.baslik || '',
                aciklama: etkinlikToEdit.aciklama || '',
                etkinlikTarihi: tarihLocal,
                yer: etkinlikToEdit.yer || etkinlikToEdit.konum || '',
                konumLink: etkinlikToEdit.konumLink || '',
                resimURL: etkinlikToEdit.resimURL || etkinlikToEdit.gorselUrl || '',
                fiyat: priceVal,
                kontenjan: etkinlikToEdit.kontenjan !== undefined ? etkinlikToEdit.kontenjan : '',
                yedekKontenjan: etkinlikToEdit.yedekKontenjan !== undefined ? etkinlikToEdit.yedekKontenjan : '10',
                kayitBaslangic: formatIsoForInput(etkinlikToEdit.kayitBaslangic),
                kayitBitis: formatIsoForInput(etkinlikToEdit.kayitBitis),
                iptalSureLimitiSaat: etkinlikToEdit.iptalSureLimitiSaat !== undefined ? etkinlikToEdit.iptalSureLimitiSaat : '24',
                katilimSarti: etkinlikToEdit.katilimSarti || (priceVal === 0 ? 'HERKES_ACIK' : 'UYE_ZORUNLU'),
            });
        } else {
            const d = new Date();
            const localIso = new Date(d.getTime() - (d.getTimezoneOffset() * 60000)).toISOString();
            setFormData({ ...initialState, etkinlikTarihi: localIso.slice(0, 16) });
            setIsFree(true);
        }
    }, [etkinlikToEdit]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value,
        }));
    };

    const handlePriceTypeChange = (free) => {
        setIsFree(free);
        if (free) {
            setFormData(prev => ({ ...prev, fiyat: '0' }));
        } else if (!formData.fiyat || formData.fiyat === '0') {
            setFormData(prev => ({ ...prev, fiyat: '50' }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setError(null);

        try {
            const isEditing = !!etkinlikToEdit;
            const kulupQuery = kulup?.kulupID ? `?kulupID=${kulup.kulupID}` : '';

            const finalPrice = isFree ? 0 : (formData.fiyat ? Number(formData.fiyat) : 0);

            const finalData = {
                baslik: formData.baslik,
                aciklama: formData.aciklama,
                tarih: formData.etkinlikTarihi,
                konum: formData.yer,
                konumLink: formData.konumLink,
                gorselUrl: formData.resimURL,
                fiyat: finalPrice,
                kontenjan: formData.kontenjan ? Number(formData.kontenjan) : 20,
                yedekKontenjan: formData.yedekKontenjan !== '' ? Number(formData.yedekKontenjan) : 10,
                kayitBaslangic: formData.kayitBaslangic || null,
                kayitBitis: formData.kayitBitis || null,
                iptalSureLimitiSaat: formData.iptalSureLimitiSaat !== '' ? Number(formData.iptalSureLimitiSaat) : 24,
                kulupID: kulup?.kulupID,
                katilimSarti: formData.katilimSarti
            };

            if (isEditing) {
                const dataToSend = { ...finalData, etkinlikID: etkinlikToEdit.etkinlikID };
                await callApi(`/etkinlikler${kulupQuery}`, 'PUT', dataToSend);
            } else {
                await callApi(`/etkinlikler${kulupQuery}`, 'POST', finalData);
            }

            onSave();
        } catch (err) {
            setError(err.message || "Etkinlik kaydı/güncellemesi başarısız oldu.");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="modern-luma-event-editor">
            <style>{`
                .modern-luma-event-editor {
                    display: flex;
                    flex-direction: column;
                    gap: 16px;
                    color: #0f172a;
                    font-family: 'Inter', -apple-system, sans-serif;
                }

                .event-header-bar {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    border-bottom: 1px solid #f1f5f9;
                    padding-bottom: 12px;
                }
                .event-header-title {
                    margin: 0;
                    font-size: 1.25rem;
                    font-weight: 800;
                    color: #0f172a;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                }
                .event-close-btn {
                    background: #f1f5f9;
                    border: none;
                    width: 32px;
                    height: 32px;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    color: #64748b;
                    transition: all 0.2s;
                }
                .event-close-btn:hover {
                    background: #e2e8f0;
                    color: #0f172a;
                }

                /* Banner */
                .event-banner-wrap {
                    position: relative;
                    width: 100%;
                    aspect-ratio: 16 / 9;
                    border-radius: 18px;
                    overflow: hidden;
                    background: #f1f5f9;
                    border: 1px solid #e2e8f0;
                }
                .event-banner-wrap img {
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                }
                .event-banner-overlay {
                    position: absolute;
                    inset: 0;
                    background: linear-gradient(180deg, rgba(0,0,0,0) 40%, rgba(0,0,0,0.65) 100%);
                    display: flex;
                    align-items: flex-end;
                    justify-content: flex-end;
                    padding: 14px;
                }
                .event-banner-btn {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    background: rgba(255, 255, 255, 0.95);
                    backdrop-filter: blur(8px);
                    color: #0f172a;
                    border: none;
                    padding: 8px 14px;
                    border-radius: 20px;
                    font-size: 0.8rem;
                    font-weight: 700;
                    cursor: pointer;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.15);
                    transition: all 0.2s ease;
                }
                .event-banner-btn:hover {
                    background: #ffffff;
                    transform: translateY(-1px);
                }

                .event-uploader-drawer {
                    background: #ffffff;
                    border: 1.5px solid #e2e8f0;
                    border-radius: 16px;
                    padding: 16px;
                    animation: fadeIn 0.2s ease;
                }

                /* Headline */
                .event-title-input {
                    width: 100%;
                    border: none;
                    background: transparent;
                    font-size: clamp(22px, 4vw, 30px);
                    font-weight: 800;
                    color: #0f172a;
                    outline: none;
                    padding: 4px 0;
                    font-family: inherit;
                    line-height: 1.25;
                }
                .event-title-input::placeholder {
                    color: #cbd5e1;
                }

                /* Luma Cards */
                .luma-card {
                    background: #ffffff;
                    border: 1px solid #e2e8f0;
                    border-radius: 16px;
                    padding: 18px 20px;
                    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.02);
                }
                .luma-card-title {
                    font-size: 0.88rem;
                    font-weight: 700;
                    color: #0f172a;
                    margin: 0 0 14px;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                }

                .luma-grid-2 {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 12px;
                }

                .luma-field-group {
                    display: flex;
                    flex-direction: column;
                    gap: 6px;
                }
                .luma-label {
                    font-size: 0.76rem;
                    font-weight: 700;
                    color: #64748b;
                    text-transform: uppercase;
                    letter-spacing: 0.03em;
                }
                .luma-input {
                    background: #f8fafc;
                    border: 1.5px solid #e2e8f0;
                    border-radius: 12px;
                    padding: 9px 12px;
                    font-size: 0.88rem;
                    color: #0f172a;
                    outline: none;
                    font-family: inherit;
                    transition: border-color 0.15s;
                }
                .luma-input:focus {
                    border-color: var(--primary, #6366f1);
                    background: #ffffff;
                }

                /* Segmented Selector */
                .segmented-toggle-bar {
                    display: flex;
                    background: #f1f5f9;
                    padding: 4px;
                    border-radius: 12px;
                    gap: 4px;
                    margin-bottom: 12px;
                }
                .segmented-toggle-btn {
                    flex: 1;
                    padding: 8px 12px;
                    border: none;
                    background: transparent;
                    border-radius: 9px;
                    font-size: 0.82rem;
                    font-weight: 700;
                    color: #64748b;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 6px;
                    transition: all 0.15s ease;
                }
                .segmented-toggle-btn.active {
                    background: #ffffff;
                    color: var(--primary, #6366f1);
                    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
                }

                /* Condition Select Cards */
                .condition-cards-grid {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 10px;
                }
                .condition-card {
                    border: 1.5px solid #e2e8f0;
                    border-radius: 14px;
                    padding: 12px 14px;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    background: #f8fafc;
                    transition: all 0.2s ease;
                }
                .condition-card.active {
                    border-color: var(--primary, #6366f1);
                    background: rgba(99, 102, 241, 0.05);
                }
                .condition-card-text h5 {
                    margin: 0 0 2px;
                    font-size: 0.86rem;
                    font-weight: 700;
                    color: #0f172a;
                }
                .condition-card-text p {
                    margin: 0;
                    font-size: 0.72rem;
                    color: #64748b;
                }

                @media (max-width: 640px) {
                    .luma-grid-2 { grid-template-columns: 1fr; }
                    .condition-cards-grid { grid-template-columns: 1fr; }
                }
            `}</style>

            <div className="drag-handle-pill"></div>

            {/* Top Header */}
            <div className="event-header-bar">
                <h3 className="event-header-title">
                    <Calendar size={20} color="#6366f1" />
                    {etkinlikToEdit ? 'Etkinliği Düzenle' : 'Yeni Etkinlik Oluştur'}
                </h3>
                <button type="button" className="event-close-btn" onClick={onCancel} aria-label="Kapat">
                    <X size={18} />
                </button>
            </div>

            {error && (
                <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', padding: '12px 14px', borderRadius: '12px', fontSize: '0.88rem' }}>
                    {error}
                </div>
            )}

            {/* 16:9 Event Poster Banner */}
            <div className="event-banner-wrap">
                <img 
                    src={formData.resimURL || "https://images.unsplash.com/photo-1511795409834-ef04bbd61622?q=80&w=1200"} 
                    alt="Etkinlik Afişi" 
                />
                <div className="event-banner-overlay">
                    <button 
                        type="button" 
                        className="event-banner-btn"
                        onClick={() => setShowUploader(!showUploader)}
                    >
                        <Camera size={15} /> {showUploader ? 'Kapat' : 'Afişi Değiştir'}
                    </button>
                </div>
            </div>

            {/* Collapsible Uploader */}
            {showUploader && (
                <div className="event-uploader-drawer">
                    <span style={{ fontSize: '0.8rem', fontWeight: 700, color: '#475569', display: 'block', marginBottom: '8px' }}>
                        Etkinlik Afişi Seçin veya Link Girin:
                    </span>
                    <ImageUploader
                        currentUrl={formData.resimURL}
                        onUploadComplete={(url) => {
                            setFormData(prev => ({ ...prev, resimURL: url }));
                            setShowUploader(false);
                        }}
                    />
                </div>
            )}

            {/* Title Input */}
            <div>
                <label className="luma-label" style={{ display: 'block', marginBottom: '4px' }}>
                    Etkinlik Başlığı <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <input
                    type="text"
                    name="baslik"
                    value={formData.baslik}
                    onChange={handleChange}
                    placeholder="Etkinlik adını buraya yazın..."
                    className="event-title-input"
                    required
                />
            </div>

            {/* CARD 1: Date & Time Schedule */}
            <div className="luma-card">
                <h4 className="luma-card-title">
                    <Clock size={16} color="#6366f1" /> Zaman ve Kayıt Aralıkları
                </h4>
                
                <div className="luma-field-group" style={{ marginBottom: '14px' }}>
                    <label className="luma-label">Etkinlik Başlangıç Zamanı <span style={{ color: '#ef4444' }}>*</span></label>
                    <input
                        type="datetime-local"
                        name="etkinlikTarihi"
                        value={formData.etkinlikTarihi}
                        onChange={handleChange}
                        className="luma-input"
                        required
                    />
                </div>

                <div className="luma-grid-2">
                    <div className="luma-field-group">
                        <label className="luma-label">Kayıt Başlangıç</label>
                        <input
                            type="datetime-local"
                            name="kayitBaslangic"
                            value={formData.kayitBaslangic}
                            onChange={handleChange}
                            className="luma-input"
                        />
                    </div>
                    <div className="luma-field-group">
                        <label className="luma-label">Kayıt Bitiş</label>
                        <input
                            type="datetime-local"
                            name="kayitBitis"
                            value={formData.kayitBitis}
                            onChange={handleChange}
                            className="luma-input"
                        />
                    </div>
                </div>
            </div>

            {/* CARD 2: Location */}
            <div className="luma-card">
                <h4 className="luma-card-title">
                    <MapPin size={16} color="#6366f1" /> Konum Bilgileri
                </h4>
                
                <div className="luma-grid-2">
                    <div className="luma-field-group">
                        <label className="luma-label">Etkinlik Yeri <span style={{ color: '#ef4444' }}>*</span></label>
                        <input
                            type="text"
                            name="yer"
                            value={formData.yer}
                            onChange={handleChange}
                            placeholder="Örn: Gastronomi Mutfak Atölyesi"
                            className="luma-input"
                            required
                        />
                    </div>
                    <div className="luma-field-group">
                        <label className="luma-label">
                            <LinkIcon size={12} style={{ display: 'inline', marginRight: '4px', verticalAlign: '-1px' }} />
                            Harita / Konum Linki
                        </label>
                        <input
                            type="url"
                            name="konumLink"
                            value={formData.konumLink}
                            onChange={handleChange}
                            placeholder="https://maps.google.com/..."
                            className="luma-input"
                        />
                    </div>
                </div>
            </div>

            {/* CARD 3: Tickets & Access Policy */}
            <div className="luma-card">
                <h4 className="luma-card-title">
                    <Ticket size={16} color="#6366f1" /> Bilet ve Katılım Koşulları
                </h4>

                {/* Free vs Paid Toggle */}
                <div className="segmented-toggle-bar">
                    <button 
                        type="button" 
                        className={`segmented-toggle-btn ${isFree ? 'active' : ''}`}
                        onClick={() => handlePriceTypeChange(true)}
                    >
                        🎁 Ücretsiz Etkinlik
                    </button>
                    <button 
                        type="button" 
                        className={`segmented-toggle-btn ${!isFree ? 'active' : ''}`}
                        onClick={() => handlePriceTypeChange(false)}
                    >
                        💳 Ücretli Etkinlik
                    </button>
                </div>

                {!isFree && (
                    <div className="luma-field-group" style={{ marginBottom: '14px' }}>
                        <label className="luma-label">Bilet Ücreti (TL)</label>
                        <input
                            type="number"
                            name="fiyat"
                            value={formData.fiyat}
                            onChange={handleChange}
                            placeholder="Örn: 50"
                            className="luma-input"
                        />
                    </div>
                )}

                {/* Membership Condition Cards */}
                <div className="condition-cards-grid">
                    <div 
                        className={`condition-card ${formData.katilimSarti === 'UYE_ZORUNLU' ? 'active' : ''}`}
                        onClick={() => setFormData(prev => ({ ...prev, katilimSarti: 'UYE_ZORUNLU' }))}
                    >
                        <ShieldCheck size={22} color={formData.katilimSarti === 'UYE_ZORUNLU' ? '#6366f1' : '#94a3b8'} />
                        <div className="condition-card-text">
                            <h5>Sadece Kulüp Üyeleri</h5>
                            <p>Dışarıdan katılıma kapalı</p>
                        </div>
                    </div>

                    <div 
                        className={`condition-card ${formData.katilimSarti === 'HERKES_ACIK' ? 'active' : ''}`}
                        onClick={() => setFormData(prev => ({ ...prev, katilimSarti: 'HERKES_ACIK' }))}
                    >
                        <Globe size={22} color={formData.katilimSarti === 'HERKES_ACIK' ? '#6366f1' : '#94a3b8'} />
                        <div className="condition-card-text">
                            <h5>Herkese Açık</h5>
                            <p>Tüm öğrenciler katılabilir</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* CARD 4: Capacity & Limits */}
            <div className="luma-card">
                <h4 className="luma-card-title">
                    <Users size={16} color="#6366f1" /> Kontenjan ve İptal Süresi
                </h4>

                <div className="luma-grid-2" style={{ marginBottom: '14px' }}>
                    <div className="luma-field-group">
                        <label className="luma-label">Asil Kontenjan</label>
                        <input
                            type="number"
                            name="kontenjan"
                            value={formData.kontenjan}
                            onChange={handleChange}
                            placeholder="Örn: 25"
                            className="luma-input"
                        />
                    </div>
                    <div className="luma-field-group">
                        <label className="luma-label">Yedek Kontenjan</label>
                        <input
                            type="number"
                            name="yedekKontenjan"
                            value={formData.yedekKontenjan}
                            onChange={handleChange}
                            placeholder="Örn: 10"
                            className="luma-input"
                        />
                    </div>
                </div>

                <div className="luma-field-group">
                    <label className="luma-label">Cezasız İptal Edebilme Süre Sınırı (Saat)</label>
                    <input
                        type="number"
                        name="iptalSureLimitiSaat"
                        value={formData.iptalSureLimitiSaat}
                        onChange={handleChange}
                        placeholder="Örn: 24"
                        className="luma-input"
                    />
                    <span style={{ fontSize: '0.74rem', color: '#94a3b8', display: 'flex', alignItems: 'center', gap: '4px', marginTop: '2px' }}>
                        <AlertCircle size={12} /> Etkinliğe bu süreden daha az kaldığında üyeler sistemden cezasız iptal yapamaz.
                    </span>
                </div>
            </div>

            {/* Description / Details */}
            <div className="luma-card">
                <h4 className="luma-card-title">
                    <Sparkles size={16} color="#6366f1" /> Etkinlik Detayları & Açıklama
                </h4>
                <AutoTextarea
                    name="aciklama"
                    minRows={3}
                    value={formData.aciklama}
                    onChange={handleChange}
                    placeholder="Etkinlik akışı, katılımcıların yanlarında getirmesi gerekenler ve özel notlar..."
                    style={{
                        border: '1.5px solid #e2e8f0',
                        borderRadius: '12px',
                        padding: '12px',
                        background: '#f8fafc',
                        fontSize: '15px'
                    }}
                />
            </div>

            {/* Sticky Actions */}
            <div className="form-sticky-actions">
                <button type="button" onClick={onCancel} className="cancel-button" disabled={isLoading}>
                    <X size={16} /> Vazgeç
                </button>
                <button type="submit" className="submit-button" disabled={isLoading}>
                    <Check size={16} /> {isLoading ? 'Kaydediliyor...' : 'Etkinliği Kaydet'}
                </button>
            </div>
        </form>
    );
};

export default EtkinlikFormu;